/**
 * Google Intelligence Dashboard
 *
 * Unified view of Google Search Console, Analytics, Ads, and Shopping data.
 *
 * PRD: docs/delivery/stories/google-enterprise-integration/prd.md
 * GitHub Issue: #378
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  BarChart3,
  Megaphone,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointer,
  DollarSign,
  Users,
  Clock,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  RefreshCw,
  Settings,
  HelpCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import { ImprovedPagesSection } from '@/components/google-intelligence/ImprovedPagesSection';
import {
  googleIntelligenceService,
  type GSCOverview,
  type GA4Overview,
  type AdsSummary,
  type ShoppingSummary,
  type GSCPage,
  type GA4Page,
  type AdsSearchTerm,
  type ShoppingProduct,
  type PositionChange,
  type ContentGap,
} from '@/services/googleIntelligenceService';
import { googleIntegrationsService, type GoogleIntegrationStatus } from '@/services/googleIntegrationsService';

// Date range options
const DATE_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

// Format helpers
const formatNumber = (n: number) => n?.toLocaleString() ?? '0';
const formatPercent = (n: number) => `${(n * 100).toFixed(1)}%`;
const formatCurrency = (n: number) => `$${n?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0.00'}`;
const formatPosition = (n: number) => n?.toFixed(1) ?? '-';

// Change indicator
function ChangeIndicator({ value, inverted = false }: { value: number | null; inverted?: boolean }) {
  if (value === null || value === undefined) return <Minus className="h-4 w-4 text-muted-foreground" />;
  const isPositive = inverted ? value < 0 : value > 0;
  const Icon = isPositive ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const color = isPositive ? 'text-green-500' : value < 0 ? 'text-red-500' : 'text-muted-foreground';
  return (
    <span className={`flex items-center ${color}`}>
      <Icon className="h-4 w-4" />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

// Metric card
function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  inverted = false,
}: {
  title: string;
  value: string;
  change?: number | null;
  icon: React.ElementType;
  inverted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Icon className="h-5 w-5 text-muted-foreground" />
            {change !== undefined && <ChangeIndicator value={change} inverted={inverted} />}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function GoogleIntelligenceDashboard() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || selectedSite?._id;

  // State
  const [status, setStatus] = useState<GoogleIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  // Data state
  const [gscOverview, setGscOverview] = useState<GSCOverview | null>(null);
  const [ga4Overview, setGa4Overview] = useState<GA4Overview | null>(null);
  const [adsSummary, setAdsSummary] = useState<AdsSummary | null>(null);
  const [shoppingSummary, setShoppingSummary] = useState<ShoppingSummary | null>(null);

  // Detail data
  const [gscPages, setGscPages] = useState<GSCPage[]>([]);
  const [ga4Pages, setGa4Pages] = useState<GA4Page[]>([]);
  const [searchTerms, setSearchTerms] = useState<AdsSearchTerm[]>([]);
  const [shoppingProducts, setShoppingProducts] = useState<ShoppingProduct[]>([]);
  const [positionChanges, setPositionChanges] = useState<PositionChange[]>([]);
  const [contentGaps, setContentGaps] = useState<ContentGap[]>([]);

  // Calculate date range
  const getDateRange = useCallback(() => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(dateRange));
    return { startDate: startDate.toISOString().split('T')[0], endDate };
  }, [dateRange]);

  // Load integration status
  const loadStatus = useCallback(async () => {
    if (!siteId) return;
    try {
      const data = await googleIntegrationsService.getStatus(siteId);
      setStatus(data);
    } catch (error) {
      console.error('Failed to load integration status:', error);
    }
  }, [siteId]);

  // Load overview data
  const loadOverviewData = useCallback(async () => {
    if (!siteId || !status?.connected) return;
    setLoading(true);

    const { startDate, endDate } = getDateRange();

    try {
      const promises: Promise<any>[] = [];

      if (status.services.gsc.connected) {
        promises.push(
          googleIntelligenceService.getGSCOverview(siteId, { startDate, endDate })
            .then(setGscOverview)
            .catch(() => setGscOverview(null))
        );
      }

      if (status.services.ga4.connected) {
        promises.push(
          googleIntelligenceService.getGA4Overview(siteId, { startDate, endDate })
            .then(setGa4Overview)
            .catch(() => setGa4Overview(null))
        );
      }

      if (status.services.ads.connected) {
        promises.push(
          googleIntelligenceService.getAdsSummary(siteId, { startDate, endDate })
            .then(setAdsSummary)
            .catch(() => setAdsSummary(null))
        );
      }

      if (status.services.merchant_center.connected) {
        promises.push(
          googleIntelligenceService.getShoppingSummary(siteId, { startDate, endDate })
            .then(setShoppingSummary)
            .catch(() => setShoppingSummary(null))
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  }, [siteId, status, getDateRange]);

  // Load detail data based on active tab
  const loadDetailData = useCallback(async () => {
    if (!siteId || !status?.connected) return;

    const { startDate, endDate } = getDateRange();

    try {
      switch (activeTab) {
        case 'gsc':
          if (status.services.gsc.connected) {
            const [pages, changes] = await Promise.all([
              googleIntelligenceService.getGSCPages(siteId, { startDate, endDate, limit: 20 }),
              googleIntelligenceService.getPositionChanges(siteId),
            ]);
            setGscPages(pages.pages);
            setPositionChanges(changes.changes);
          }
          break;

        case 'ga4':
          if (status.services.ga4.connected) {
            const pages = await googleIntelligenceService.getGA4Pages(siteId, { startDate, endDate, limit: 20 });
            setGa4Pages(pages.pages);
          }
          break;

        case 'ads':
          if (status.services.ads.connected) {
            const [terms, gaps] = await Promise.all([
              googleIntelligenceService.getAdsSearchTerms(siteId, { startDate, endDate, limit: 20 }),
              googleIntelligenceService.getContentGaps(siteId),
            ]);
            setSearchTerms(terms.searchTerms);
            setContentGaps(gaps.gaps);
          }
          break;

        case 'shopping':
          if (status.services.merchant_center.connected) {
            const products = await googleIntelligenceService.getShoppingProducts(siteId, {
              startDate,
              endDate,
              sortBy: 'impressions',
              limit: 20,
            });
            setShoppingProducts(products.products);
          }
          break;
      }
    } catch (error) {
      console.error('Failed to load detail data:', error);
    }
  }, [siteId, status, activeTab, getDateRange]);

  // Initial load
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.connected) {
      loadOverviewData();
    } else {
      setLoading(false);
    }
  }, [status, loadOverviewData]);

  useEffect(() => {
    if (status?.connected && activeTab !== 'overview') {
      loadDetailData();
    }
  }, [status, activeTab, loadDetailData]);

  // Refresh handler
  const handleRefresh = () => {
    loadOverviewData();
    if (activeTab !== 'overview') {
      loadDetailData();
    }
  };

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view Google intelligence data.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading && !status) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Connect Google Services</CardTitle>
            <CardDescription>
              Connect your Google account to see Search Console, Analytics, Ads, and Shopping data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/settings/integrations">
              <Button>
                <Settings className="h-4 w-4 mr-2" />
                Go to Integrations
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Google Intelligence</h1>
          <p className="text-muted-foreground">
            Unified view of your Google data for {selectedSite?.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Connected Services */}
      <div className="flex gap-2">
        {status.services.gsc.connected && (
          <Badge variant="secondary"><Search className="h-3 w-3 mr-1" /> Search Console</Badge>
        )}
        {status.services.ga4.connected && (
          <Badge variant="secondary"><BarChart3 className="h-3 w-3 mr-1" /> Analytics</Badge>
        )}
        {status.services.ads.connected && (
          <Badge variant="secondary"><Megaphone className="h-3 w-3 mr-1" /> Ads</Badge>
        )}
        {status.services.merchant_center.connected && (
          <Badge variant="secondary"><ShoppingCart className="h-3 w-3 mr-1" /> Shopping</Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {status.services.gsc.connected && <TabsTrigger value="gsc">Search Console</TabsTrigger>}
          {status.services.ga4.connected && <TabsTrigger value="ga4">Analytics</TabsTrigger>}
          {status.services.ads.connected && <TabsTrigger value="ads">Ads</TabsTrigger>}
          {status.services.merchant_center.connected && <TabsTrigger value="shopping">Shopping</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : (
            <>
              {/* GSC Metrics */}
              {gscOverview && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Search className="h-5 w-5" /> Search Console
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard title="Impressions" value={formatNumber(gscOverview.totalImpressions)} icon={Eye} />
                    <MetricCard title="Clicks" value={formatNumber(gscOverview.totalClicks)} icon={MousePointer} />
                    <MetricCard title="Avg CTR" value={formatPercent(gscOverview.avgCTR)} icon={TrendingUp} />
                    <MetricCard title="Avg Position" value={formatPosition(gscOverview.avgPosition)} icon={BarChart3} inverted />
                  </div>
                </div>
              )}

              {/* GA4 Metrics */}
              {ga4Overview && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" /> Analytics
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard title="Sessions" value={formatNumber(ga4Overview.totalSessions)} icon={Users} />
                    <MetricCard title="Users" value={formatNumber(ga4Overview.totalUsers)} icon={Users} />
                    <MetricCard title="Engagement Rate" value={formatPercent(ga4Overview.avgEngagementRate)} icon={TrendingUp} />
                    <MetricCard title="Bounce Rate" value={formatPercent(ga4Overview.avgBounceRate)} icon={TrendingDown} inverted />
                  </div>
                </div>
              )}

              {/* Ads Metrics */}
              {adsSummary && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Megaphone className="h-5 w-5" /> Google Ads
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard title="Search Terms" value={formatNumber(adsSummary.totalTerms)} icon={Search} />
                    <MetricCard title="Clicks" value={formatNumber(adsSummary.totalClicks)} icon={MousePointer} />
                    <MetricCard title="Cost" value={formatCurrency(adsSummary.totalCost)} icon={DollarSign} />
                    <MetricCard title="Questions Found" value={formatNumber(adsSummary.questionCount)} icon={HelpCircle} />
                  </div>
                </div>
              )}

              {/* Shopping Metrics */}
              {shoppingSummary && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" /> Shopping
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard title="Products" value={formatNumber(shoppingSummary.totalProducts)} icon={ShoppingCart} />
                    <MetricCard title="Revenue" value={formatCurrency(shoppingSummary.totalRevenue)} icon={DollarSign} />
                    <MetricCard title="ROAS" value={shoppingSummary.avgROAS?.toFixed(2) || '-'} icon={TrendingUp} />
                    <MetricCard title="Enriched" value={formatNumber(shoppingSummary.enrichedProductCount)} icon={BarChart3} />
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* GSC Tab */}
        <TabsContent value="gsc" className="mt-4 space-y-6">
          {/* PageMind-Improved Pages — per-page deltas around deployment date. */}
          {siteId && <ImprovedPagesSection siteId={siteId} />}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            <Card>
              <CardHeader>
                <CardTitle>Top Pages</CardTitle>
                <CardDescription>Pages with most impressions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page</TableHead>
                      <TableHead className="text-right">Impressions</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Position</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(gscPages || []).slice(0, 10).map((page, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[300px] truncate" title={page.pageUrl}>
                          {(() => { try { return new URL(page.pageUrl).pathname; } catch { return page.pageUrl; } })()}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(page.totalImpressions)}</TableCell>
                        <TableCell className="text-right">{formatNumber(page.totalClicks)}</TableCell>
                        <TableCell className="text-right">{formatPosition(page.avgPosition)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Position Changes */}
            <Card>
              <CardHeader>
                <CardTitle>Position Changes</CardTitle>
                <CardDescription>Significant ranking movements</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Query</TableHead>
                      <TableHead className="text-right">Before</TableHead>
                      <TableHead className="text-right">After</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(positionChanges || []).slice(0, 10).map((change, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[200px] truncate">{change.query}</TableCell>
                        <TableCell className="text-right">{formatPosition(change.previousPosition)}</TableCell>
                        <TableCell className="text-right">{formatPosition(change.currentPosition)}</TableCell>
                        <TableCell className={`text-right ${change.improved ? 'text-green-500' : 'text-red-500'}`}>
                          {change.improved ? '-' : '+'}{Math.abs(change.change).toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* GA4 Tab */}
        <TabsContent value="ga4" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Pages by Sessions</CardTitle>
              <CardDescription>Pages with most traffic</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Page Views</TableHead>
                    <TableHead className="text-right">Bounce Rate</TableHead>
                    <TableHead className="text-right">Conversions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ga4Pages || []).map((page, i) => (
                    <TableRow key={i}>
                      <TableCell className="max-w-[300px] truncate">{page.pagePath}</TableCell>
                      <TableCell className="text-right">{formatNumber(page.totalSessions)}</TableCell>
                      <TableCell className="text-right">{formatNumber(page.totalPageViews)}</TableCell>
                      <TableCell className="text-right">{formatPercent(page.avgBounceRate)}</TableCell>
                      <TableCell className="text-right">{formatNumber(page.totalConversions)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ads Tab */}
        <TabsContent value="ads" className="mt-4 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Search Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Top Search Terms</CardTitle>
                <CardDescription>What customers search for</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Term</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-center">Question?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(searchTerms || []).slice(0, 10).map((term, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[200px] truncate">{term.searchTerm}</TableCell>
                        <TableCell className="text-right">{formatNumber(term.clicks)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(term.cost)}</TableCell>
                        <TableCell className="text-center">
                          {term.isQuestion && <Badge variant="secondary">Yes</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Content Gaps */}
            <Card>
              <CardHeader>
                <CardTitle>Content Gaps</CardTitle>
                <CardDescription>Paid queries without organic coverage</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Term</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Potential Savings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(contentGaps || []).slice(0, 10).map((gap, i) => (
                      <TableRow key={i}>
                        <TableCell className="max-w-[200px] truncate">{gap.searchTerm}</TableCell>
                        <TableCell className="text-right">{formatCurrency(gap.cost)}</TableCell>
                        <TableCell className="text-right text-green-500">{formatCurrency(gap.potentialSavings)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Shopping Tab */}
        <TabsContent value="shopping" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Products</CardTitle>
              <CardDescription>Products with most impressions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Impressions</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-center">Enriched</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(shoppingProducts || []).map((product, i) => (
                    <TableRow key={i}>
                      <TableCell className="max-w-[300px] truncate">{product.productTitle}</TableCell>
                      <TableCell className="text-right">{formatNumber(product.impressions)}</TableCell>
                      <TableCell className="text-right">{formatNumber(product.clicks)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(product.conversionValue)}</TableCell>
                      <TableCell className="text-center">
                        {product.hasEnrichedContent && <Badge variant="default">Yes</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
